import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/cn';

export interface MenuItem {
  key: string;
  label: string;
  icon?: ReactNode;
  destructive?: boolean;
  disabled?: boolean;
  onSelect: () => void;
}

export interface MenuProps {
  trigger: ReactNode;
  items: MenuItem[];
  align?: 'start' | 'end';
  ariaLabel: string;
  header?: ReactNode;
}

export function Menu({ trigger, items, align = 'end', ariaLabel, header }: MenuProps) {
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const enabled = useMemo(() => items.filter((i) => !i.disabled), [items]);

  const measure = () =>
    (triggerRef.current?.firstElementChild ?? triggerRef.current)?.getBoundingClientRect() ?? null;

  useLayoutEffect(() => {
    if (open) setRect(measure());
  }, [open]);

  useEffect(() => {
    if (!open) return;

    function onPointerDown(e: PointerEvent) {
      const target = e.target as Node;
      if (!menuRef.current?.contains(target) && !triggerRef.current?.contains(target)) {
        setOpen(false);
      }
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setOpen(false);
        return;
      }
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex((i) => {
          const next = e.key === 'ArrowDown' ? i + 1 : i - 1;
          return (next + enabled.length) % enabled.length;
        });
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        enabled[activeIndex]?.onSelect();
        setOpen(false);
      }
    }

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    const reposition = () => setRect(measure());
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);

    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open, activeIndex, enabled]);

  return (
    <>
      <div
        ref={triggerRef}
        onClick={() => {
          setActiveIndex(0);
          setOpen((o) => !o);
        }}
        className="contents"
      >
        {trigger}
      </div>

      {open &&
        rect &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            aria-label={ariaLabel}
            style={{
              top: rect.bottom + 4,
              left: align === 'end' ? undefined : rect.left,
              right: align === 'end' ? window.innerWidth - rect.right : undefined,
            }}
            className="card fixed z-50 min-w-44 overflow-hidden p-1 shadow-lg"
          >
            {header}
            {items.map((item) => (
              <button
                key={item.key}
                type="button"
                role="menuitem"
                disabled={item.disabled}
                onMouseEnter={() => setActiveIndex(enabled.indexOf(item))}
                onClick={() => {
                  item.onSelect();
                  setOpen(false);
                }}
                className={cn(
                  'flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm transition-colors',
                  'disabled:cursor-not-allowed disabled:opacity-50',
                  item.destructive ? 'text-danger-600' : 'text-neutral-700',
                  enabled[activeIndex] === item &&
                    (item.destructive ? 'bg-danger-50 dark:bg-danger-900' : 'bg-neutral-100'),
                )}
              >
                {item.icon}
                {item.label}
              </button>
            ))}
          </div>,
          document.body,
        )}
    </>
  );
}
